import { BodySmall, ChevronRightIcon, HeadingSmall, OpenIcon, TeamIcon, PercentageIcon, theme } from '@arnold/common';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnswerGroup, NewReportData, QuestionSummary } from '../types';
import { questionSummaryKey } from '@arnold/core';

type Question = NewReportData['questions'][number];

interface FreetextCardProps {
  question: Question;
  summary?: QuestionSummary;
  participants: AnswerGroup[];
  eligibleCount: number;
  onDetailClick?: () => void;
}

interface Comment {
  id: string;
  author: string | null;
  teamName: string | null;
  text: string;
}

interface Keyword {
  label: string;
  count: number;
}

const COMMENT_DISPLAY_LIMIT = 6;
const VISIBLE_KEYWORDS_LIMIT = 5;
const COMMENT_PREVIEW_LENGTH = 220;

// Only the data adapter changed: preserve the original card markup and visual design.
const buildComments = (participants: AnswerGroup[], question: Question): Comment[] =>
  participants.flatMap((group) =>
    (group.questionSummaries.get(questionSummaryKey(question.index))?.textEntries ?? []).map(({ message }) => ({
      id: message.id,
      author: group.type === 'individual' ? group.label : null,
      teamName: group.teamNames.join(', ') || null,
      text: message.text ?? '',
    })),
  );

export const FreetextCard = ({ question, summary, participants, eligibleCount, onDetailClick }: FreetextCardProps) => {
  const { t } = useTranslation('newReport');
  const [expandedCommentIds, setExpandedCommentIds] = useState<Set<string>>(new Set());

  const toggleCommentExpanded = (commentId: string) => {
    setExpandedCommentIds((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const comments = useMemo(() => buildComments(participants, question), [participants, question]);
  const answerCount = summary?.respondingChannelIds.size ?? 0;
  const responseRate = eligibleCount ? Math.round((100 * answerCount) / eligibleCount) : 0;
  const keywords: Keyword[] = summary?.themes.text.filter((keyword) => keyword.label && keyword.count > 1) ?? [];
  const visibleKeywords = keywords.slice(0, VISIBLE_KEYWORDS_LIMIT);
  const hiddenKeywordsCount = keywords.length - visibleKeywords.length;

  const visibleComments = comments.slice(0, COMMENT_DISPLAY_LIMIT);

  return (
    <CardWrapper data-cy="freetext-card">
      <HeaderContainer>
        <IconBadge>
          <OpenIcon />
        </IconBadge>
        <Header>
          <HeadingSmall margin="0">{question.text}</HeadingSmall>
          <Subtext>
            <span>{t('freetextCard.openType')}</span>
            <IconText>
              <TeamIcon />
              {t('freetextCard.responseCount', { count: answerCount, postProcess: 'interval' })}
            </IconText>
            <IconText>
              <PercentageIcon />
              {t('freetextCard.responseRate', { rate: responseRate })}
            </IconText>
          </Subtext>
        </Header>
        {onDetailClick && (
          <DetailButton
            type="button"
            onClick={onDetailClick}
            aria-label={t('freetextCard.detailButtonLabel')}
            data-cy="freetext-card-detail-button"
          >
            <ChevronRightIcon />
          </DetailButton>
        )}
      </HeaderContainer>

      {keywords.length > 0 && (
        <KeywordsSection>
          <BodySmall margin="0 0 8px" bold>
            {t('freetextCard.aiThemesTitle')}
          </BodySmall>
          <KeywordsList>
            {visibleKeywords.map((keyword) => (
              <KeywordPill key={keyword.label}>
                {keyword.label}
                <KeywordCount>{keyword.count}</KeywordCount>
              </KeywordPill>
            ))}
            {hiddenKeywordsCount > 0 && <HiddenKeywordsCount>+{hiddenKeywordsCount}</HiddenKeywordsCount>}
          </KeywordsList>
        </KeywordsSection>
      )}

      <CommentsGrid>
        {visibleComments.map((comment) => {
          const isExpanded = expandedCommentIds.has(comment.id);
          const isTruncatable = comment.text.length > COMMENT_PREVIEW_LENGTH;
          const displayedText =
            isExpanded || !isTruncatable ? comment.text : `${comment.text.slice(0, COMMENT_PREVIEW_LENGTH).trimEnd()}…`;

          return (
            <CommentCard key={comment.id}>
              <BodySmall margin="0" bold>
                {comment.author ?? t('freetextCard.anonymous')}
              </BodySmall>
              {comment.teamName && (
                <BodySmall margin="0" color={theme.colors.text.secondary}>
                  {t('freetextCard.team', { teamName: comment.teamName })}
                </BodySmall>
              )}
              <CommentQuote>{displayedText}</CommentQuote>
              {isTruncatable && (
                <ToggleCommentButton type="button" onClick={() => toggleCommentExpanded(comment.id)}>
                  {isExpanded ? t('freetextCard.showLess') : t('freetextCard.showMore')}
                </ToggleCommentButton>
              )}
            </CommentCard>
          );
        })}
      </CommentsGrid>

      {comments.length > 0 && (
        <CommentsFooter>
          {t('freetextCard.commentsShown', { shown: visibleComments.length, total: comments.length })}
        </CommentsFooter>
      )}
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.backgroundCover.default};
  border-radius: ${theme.borderRadius.l};
  border: 1px solid ${theme.colors.borderSeparator.default};
  margin-bottom: ${theme.spacing.d};
`;

const HeaderContainer = styled.div`
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
  padding: ${theme.spacing.g};
  display: flex;
  gap: ${theme.spacing.f};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.m};
  background-color: ${theme.colors.emotionInformative.background};
`;

const Subtext = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.f};
  font-family: 'Roboto', sans-serif;
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing.e};
`;

const IconText = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.c};
`;

const DetailButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${theme.colors.brand.primary};
  background-color: transparent;
  color: ${theme.colors.brand.primary};
  cursor: pointer;
  transition:
    background-color 0.15s ease-in-out,
    color 0.15s ease-in-out;

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    fill: currentColor;
  }

  &:hover {
    background-color: ${theme.colors.brand.primary};
    color: ${theme.colors.textInverted.primary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.focused};
    outline-offset: 2px;
  }
`;

const KeywordsSection = styled.div`
  padding: ${theme.spacing.g};
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
`;

const KeywordsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.d};
`;

const KeywordPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
  padding: ${theme.spacing.c} ${theme.spacing.e};
  border-radius: ${theme.borderRadius.xxl};
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.emotionInformative.hover};
  background-color: ${theme.colors.emotionInformative.background};
`;

const KeywordCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: ${theme.borderRadius.xxl};
  background-color: #e3f5fa;
  color: ${theme.colors.emotionInformative.hover};
  font-size: 11px;
  font-weight: ${theme.typography.body.small.bold.fontWeight};
`;

const CommentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  @container overview (max-width: 650px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${theme.breakpoint.small}) {
    grid-template-columns: 1fr;
  }
`;

const CommentCard = styled.div`
  padding: ${theme.spacing.g};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
  border: 1px solid ${theme.colors.borderSeparator.default};
`;

const CommentQuote = styled(BodySmall)`
  position: relative;
  padding-left: ${theme.spacing.f};
  color: ${theme.colors.text.primary};

  &:before {
    position: absolute;
    left: 0;
    top: -0.15em;
    content: '„';
    font-weight: 300;
    font-size: 28px;
    line-height: 1;
    color: ${theme.colors.text.disabled};
  }
`;

const ToggleCommentButton = styled.button`
  align-self: flex-end;
  padding: 0;
  border: none;
  background: none;
  color: ${theme.colors.brand.primary};
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.bold.fontWeight};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CommentsFooter = styled.div`
  padding: ${theme.spacing.e} ${theme.spacing.g};
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.secondary};
`;

const HiddenKeywordsCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.emotionInformative.hover};
  font-size: 11px;
  font-weight: ${theme.typography.body.small.bold.fontWeight};
`;
